import { isBranchConstrained } from "../../../../auth/sessionAccess.ts";
import React, { useEffect, useState } from "react";
import { readBranches } from "../../../../services/branch.service.ts";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/index.ts";
import Branch from "../../../../models/Branch.ts";

interface BranchSelectorProps {
  currentId?: string | number;
  variant?: "row" | "simple";
}

const BranchSelector = ({
  currentId,
  variant = "row",
}: BranchSelectorProps) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const data = await readBranches();
        setBranches(data);
      } catch (error) {
        console.error("Error fetching branches:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    if (!newId) return;

    const path = location.pathname;

    // Special handling for birthdays
    if (path.includes("birthdates")) {
      navigate(`/sucursales/birthdates/${newId}`);
      return;
    }

    // Special handling for incidents
    if (path.includes("incidencias")) {
      navigate(`/sucursal/incidencias/${newId}`);
      return;
    }

    const pathParts = path.split("/");
    const lastPart = pathParts[pathParts.length - 1];

    // If last part is a number, replace it
    if (!isNaN(Number(lastPart)) && lastPart !== "") {
      pathParts[pathParts.length - 1] = newId;
      navigate(pathParts.join("/"));
    } else {
      // If it's a generic path, append the ID OR navigate to a specific child
      // For sucursales/editar etc
      if (path.endsWith("sucursales")) {
        navigate(`/sucursales/editar/${newId}`);
      } else {
        // Fallback: try to see if appending works (standard for most of our routes)
        navigate(`${path}/${newId}`);
      }
    }
  };

  if (isBranchConstrained(user)) return null;

  const selectElement = (
    <select
      className="form-select shadow-none"
      value={currentId || ""}
      onChange={handleChange}
      disabled={loading}
      style={{ fontSize: "14px", height: "40px" }}
    >
      <option value="" disabled>
        {loading ? "Cargando..." : "-- Selecciona --"}
      </option>
      {branches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
    </select>
  );

  if (variant === "simple") {
    return selectElement;
  }

  return (
    <div className="row mb- align-items-center">
      <label className="col-form-label col-md-2 text-end fw-bold">
        Sucursal:
      </label>
      <div className="col-md-3">{selectElement}</div>
    </div>
  );
};

export default BranchSelector;
